'use client'

import { InfoCircleFilled24 } from '@vinted/monochrome-icons'
import { Card, Cell, Divider, Icon, InputText, Spacer } from '@vinted/web-ui'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { ErrorItem } from '@marketplace-web/shared/api-client'
import { useTranslate } from '@marketplace-web/shared/i18n'
import {
  renderValidation,
  SeparatedList,
  useFormValidationMessage,
} from '@marketplace-web/shared/ui-helpers'
import { isValueInObject } from '@marketplace-web/shared/utils'

import { BusinessRegistrationModel } from 'types/models/business-registration'
import { CountryModel } from 'types/models/country'

import { NewBusinessAddressField } from '../../../../../../constants'
import { GeneralError } from '../../../../GeneralError'
import { Heading } from '../../../../Heading'
import { Section } from '../../../../Section'
import useCityOptions from '../../../useCityOptions'
import { camelCase } from '../../../utils'
import { InputDropdownController, InputDropdownOption } from '../InputDropdownController'

const TRANSLATION_PREFIX = 'business.registration'

type Props = {
  countryOptions: Array<InputDropdownOption>
  selectedCountry: CountryModel | undefined
  defaultValues: BusinessRegistrationModel
  apiErrors: Array<ErrorItem> | null
  showGeneralError: boolean
}

const BusinessAddressInitialDetails = ({
  countryOptions,
  selectedCountry,
  apiErrors = [],
  defaultValues,
  showGeneralError,
}: Props) => {
  const translate = useTranslate(`${TRANSLATION_PREFIX}.addresses`)
  const [defaultCity, setDefaultCity] = useState<string>('')

  const {
    register,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors },
  } = useFormContext<Partial<BusinessRegistrationModel>>()

  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)
  const postalCode = watch(NewBusinessAddressField.PostalCode)
  const { cityOptions, fullPostalCode } = useCityOptions(selectedCountry, postalCode)

  useEffect(() => {
    if (cityOptions.length > 0) {
      setDefaultCity(cityOptions[0]!.value)
    }
  }, [cityOptions])

  useEffect(() => {
    setValue(NewBusinessAddressField.FullPostalCode, fullPostalCode || '')
  }, [fullPostalCode, setValue])

  useEffect(() => {
    apiErrors?.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, NewBusinessAddressField)) return

      setError(fieldName, {
        type: 'manual',
        message: value,
      })
    })
  }, [setError, apiErrors])

  useEffect(() => {
    if (!defaultValues.businessDetails || !defaultValues.addresses) return
    reset({ businessDetails: defaultValues.businessDetails, addresses: defaultValues.addresses })
  }, [reset, defaultValues])

  return (
    <SeparatedList separator={<Spacer size={Spacer.Size.X2Large} />}>
      <Heading
        styling={Cell.Styling.Tight}
        title={translate('title')}
        subtitle={translate('subtitle')}
      />
      {showGeneralError && <GeneralError />}
      <Card>
        <div className="u-overflow-hidden">
          <Cell
            prefix={
              <Icon
                name={InfoCircleFilled24}
                color={Icon.Color.Primary}
                aria={{ 'aria-hidden': 'true' }}
              />
            }
          >
            {translate('note')}
          </Cell>
        </div>
      </Card>
      <Section>
        <SeparatedList separator={<Divider />}>
          <InputDropdownController
            title={translate('business_address.country_id.title')}
            options={countryOptions}
            input={NewBusinessAddressField.Country}
            translationPrefix={TRANSLATION_PREFIX}
            required
            disabled
          />
          <InputText
            {...register(NewBusinessAddressField.AddressLine1, {
              required: true,
            })}
            styling={InputText.Styling.Wide}
            title={translate('business_address.line1.title')}
            placeholder={translate('business_address.line1.placeholder')}
            validation={renderValidation(validationMessage(NewBusinessAddressField.AddressLine1))}
          />
          <InputText
            {...register(NewBusinessAddressField.AddressLine2)}
            styling={InputText.Styling.Wide}
            title={translate('business_address.line2.title')}
            placeholder={translate('business_address.line2.placeholder')}
            validation={renderValidation(validationMessage(NewBusinessAddressField.AddressLine2))}
          />
          <InputText
            {...register(NewBusinessAddressField.PostalCode, {
              required: true,
            })}
            styling={InputText.Styling.Wide}
            title={translate('business_address.postal_code.title')}
            placeholder={translate('business_address.postal_code.placeholder')}
            validation={renderValidation(validationMessage(NewBusinessAddressField.PostalCode))}
          />
          <InputDropdownController
            title={translate('business_address.city.title')}
            options={cityOptions}
            input={NewBusinessAddressField.City}
            translationPrefix={TRANSLATION_PREFIX}
            defaultValue={defaultCity}
            required
          />
        </SeparatedList>
      </Section>
    </SeparatedList>
  )
}

export default BusinessAddressInitialDetails
